// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import styles from './HistoryCollectComplete.module.scss';
import { useEffect, useRef, useState } from 'react';
import { createAxios } from 'ts/createAxios';
import {
  ByTenant,
  CollectHistory,
  CollectHistoryListResponse,
} from 'util/Types';
import { LabelContents } from 'components/molecules/LabelContents';
import {
  getFormatCurrentDate,
  getFormatCurrentDateTime,
  getFormatDateWithCurrentTimezone,
} from 'ts/formatDate';
import { useTranslation } from 'react-i18next';
import { DATE_FORMAT } from 'util/ConstantValues';
import { getCollectTenantMapper, normalizeNumber } from 'util/commons';
import { IconDownload } from 'components/atoms/icons/IconDownload';
import { getCollectHistoryCSVDataGroupedByTenant } from 'util/csvExportUtils';
import { handleError } from 'util/errorHandler';
import { getCollectDeliveries } from 'apis/collects';
import CustomPagination from 'components/atoms/CustomPagination';
import CollectDeliveriesDataModal from 'components/molecules/CollectDeliveriesDataModal';
import CustomCSVLink from 'components/atoms/CustomCsvLink';

export type Props = {
  id?: string;
};

export const HistoryCollectComplete = () => {
  const { t } = useTranslation();
  const [collectedData, setCollectedData] = useState<CollectHistory[]>([]);
  const [pageMeta, setPageMeta] = useState<{
    page: number;
    total: number;
  }>({
    page: 1,
    total: 0,
  });
  const [emissionDetailCollectId, setEmissionDetailCollectId] =
    useState<string>();
  const [exportCsvCollectData, setExportCsvCollectData] = useState<{
    id: string;
    targetIds: string[];
    byTenant: ByTenant;
    wasteUnitName: string | undefined;
  }>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const csvRef = useRef<any>(null);

  const pageCount = Math.ceil(pageMeta.total / 200);

  const callApi = async (page = pageMeta.page) => {
    try {
      const res = await createAxios({
        prefix: 'v2',
      }).get<CollectHistoryListResponse>('/work-histories/collect/delivered', {
        params: {
          page,
          take: 200,
        },
      });

      setCollectedData(res.data.deliveries);
      setPageMeta({
        page: res.data.page,
        total: res.data.total,
      });
    } catch (e) {
      handleError(e);
    }
  };

  const handlePageClick = (selectedItem: { selected: number }) => {
    callApi(selectedItem.selected + 1);
  };

  const handleExportCSV = async (collect: CollectHistory) => {
    await getCollectDeliveries({
      deliveryIds: [collect.id],
      cursor: null,
      successCallback: (data) => {
        setExportCsvCollectData({
          id: collect.id,
          targetIds: data
            .map((item) => item.processingObjectId)
            .filter(Boolean),
          byTenant: getCollectTenantMapper(data)[collect.id] || {},
          wasteUnitName: data.find(
            ({ deliveryId }) => deliveryId === collect.id
          )?.wasteUnitName,
        });

        setTimeout(() => {
          csvRef.current?.link?.click();
          setExportCsvCollectData(undefined);
        }, 100);
      },
      failedCallback: () => {
        setExportCsvCollectData(undefined);
      },
    });
  };

  useEffect(() => {
    callApi();
  }, []);

  const renderContentList = () => {
    if (!collectedData.length) {
      return <div className={styles.noData}>{t('messages.M_008')}</div>;
    }

    return collectedData.map((collect, index) => {
      return (
        <div className={styles.historyCollectCompleteCard} key={index}>
          <div className={styles.historyCollectCompleteContentCol}>
            <LabelContents
              label={t('common_history_field.date_time')}
              contents={
                collect.deliveredDatetime &&
                getFormatDateWithCurrentTimezone(
                  collect.deliveredDatetime,
                  DATE_FORMAT.slaYMDHm
                )
              }
            />
            <LabelContents
              label={t('common_history_field.responsible_person')}
              contents={collect.userId + '(' + collect.userName + ')'}
            />
            <LabelContents
              label={t('common_history_field.place')}
              contents={collect.areaName}
            />
            <LabelContents
              label={t('common_history_field.material_name')}
              contents={collect.description}
            />
            <LabelContents
              label={t('common_history_field.item')}
              contents={collect.wasteUnitName}
            />
            <LabelContents
              label={t('common_history_field.weight')}
              contents={
                normalizeNumber({
                  value: collect.weight,
                }) + 'kg'
              }
            />
          </div>
          <div className={styles.historyCollectCompleteActionCol}>
            <button
              onClick={() => setEmissionDetailCollectId(collect.id)}
              className={styles.viewButton}
            >
              {t('common.button.view_details')}
            </button>

            <div
              className={styles.download}
              onClick={() => handleExportCSV(collect)}
            >
              <IconDownload strokeColor="black" />
            </div>

            {exportCsvCollectData?.id === collect.id && (
              <CustomCSVLink
                className={styles.csvLink}
                ref={csvRef}
                filename={`${t(
                  'histories.csv_file_name_collect_by_waste_unit'
                )}_${getFormatCurrentDate()}`}
                data={getCollectHistoryCSVDataGroupedByTenant({
                  type: 'delivered',
                  data: collect,
                  targetIds: exportCsvCollectData.targetIds,
                  byTenant: exportCsvCollectData.byTenant,
                  exportDatetimeString: getFormatCurrentDateTime(),
                  wasteUnitName: exportCsvCollectData.wasteUnitName,
                  t,
                })}
              />
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <div className={styles.historyCollectCompletes}>
      <div className={styles.historyCollectedContent}>
        {renderContentList()}
      </div>

      <div className={styles.paginationContainer}>
        <CustomPagination
          pageCount={pageCount}
          onPageChange={handlePageClick}
          forcePage={pageMeta.page - 1}
        />
      </div>

      <CollectDeliveriesDataModal
        collectId={emissionDetailCollectId}
        onClose={() => {
          setEmissionDetailCollectId(undefined);
        }}
      />
    </div>
  );
};
